import { useEffect, useMemo, useState } from 'react';
import type { RaRecord } from 'react-admin';
import { Alert, Button, Stack, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { sessionToken } from '../session/sessionToken';
import { normalizeContentId, prepareEmailHtml } from './emailStructure';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';

/** Obraz w treści większy niż to nie trafia do podglądu (zostaje na liście załączników). */
export const INLINE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
/** Najwięcej obrazów `cid:` pobieranych na jeden mail. */
export const INLINE_IMAGE_MAX_COUNT = 20;

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/**
 * Obrazy `cid:` z treści — pobrane naszą trasą pliku z uprawnieniem (token
 * konta, jak każdy załącznik) i podane ramce jako `data:`. Ramka nie ma
 * dostępu do tokenu ani do naszego pochodzenia, więc innej drogi nie ma.
 */
function useCidImages(emailId: number, attachments: RaRecord[], html: string): Record<string, string> {
  const [images, setImages] = useState<Record<string, string>>({});
  const wanted = useMemo(() => {
    const lower = html.toLowerCase();
    return attachments
      .filter((a) => {
        const cid = normalizeContentId(a.contentId as string | undefined);
        const type = String(a.contentType ?? '').toLowerCase();
        return cid && lower.includes(`cid:${cid}`) && (type === '' || type.startsWith('image/')) && Number(a.size ?? 0) <= INLINE_IMAGE_MAX_BYTES;
      })
      .slice(0, INLINE_IMAGE_MAX_COUNT);
  }, [attachments, html]);
  useEffect(() => {
    let cancelled = false;
    const token = sessionToken();
    Promise.all(
      wanted.map(async (a) => {
        try {
          const r = await fetch(`${API}/emails/${emailId}/attachments/${a.id as number}/file`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!r.ok) return null;
          const blob = await r.blob();
          // Tylko obraz — inny plik z tym samym Content-ID nie trafia do treści.
          if (!blob.type.startsWith('image/') || blob.type.includes('svg')) return null;
          return [normalizeContentId(a.contentId as string), await readAsDataUrl(blob)] as const;
        } catch {
          return null;
        }
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const p of pairs) if (p) next[p[0]] = p[1];
      setImages(next);
    });
    return () => {
      cancelled = true;
    };
  }, [emailId, wanted]);
  return images;
}

/**
 * Treść maila w ramce bez skryptów (`sandbox`), oczyszczona DOMPurify,
 * z obrazami `cid:` z załączników i zablokowanymi obrazami z sieci
 * (przełącznik `emailDetails`, S-03.11-02). Tekst bez HTML — jak dotąd.
 */
export const EmailBody = ({
  emailId,
  html,
  text,
  attachments,
}: {
  emailId: number;
  html: string | null;
  text: string | null;
  attachments: RaRecord[];
}) => {
  const [allowRemote, setAllowRemote] = useState(false);
  const cidImages = useCidImages(emailId, attachments, html ?? '');
  const prepared = useMemo(
    () => (html ? prepareEmailHtml(html, { cidImages, allowRemote }) : null),
    [html, cidImages, allowRemote],
  );
  const srcDoc =
    prepared?.html ??
    `<pre style="font-family:system-ui,sans-serif;white-space:pre-wrap;margin:0">${(text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')}</pre>`;
  return (
    <Stack spacing={1} data-testid="email-body">
      {prepared && (prepared.blockedRemote > 0 || allowRemote) && (
        <Alert
          severity="info"
          icon={<ImageIcon fontSize="small" />}
          data-testid="email-remote-images"
          data-blocked={String(prepared.blockedRemote)}
          action={
            <Button size="small" onClick={() => setAllowRemote((v) => !v)} data-testid="email-remote-images-toggle">
              {allowRemote ? 'Ukryj obrazy z sieci' : 'Pokaż obrazy z sieci'}
            </Button>
          }
        >
          {allowRemote
            ? 'Obrazy z sieci są pokazane — nadawca może zobaczyć, że mail został otwarty.'
            : `Zablokowano obrazy z sieci (${prepared.blockedRemote}) — chronią przed śledzeniem otwarcia maila. Obrazy z załączników są pokazane.`}
        </Alert>
      )}
      <iframe
        title="email-body"
        srcDoc={srcDoc}
        sandbox=""
        data-testid="email-body-frame"
        style={{ width: '100%', height: 620, border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff' }}
      />
      {prepared && prepared.missingCid.length > 0 && Object.keys(cidImages).length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Nie wszystkie obrazy z treści są w załącznikach maila.
        </Typography>
      )}
    </Stack>
  );
};
